/**
 * Ohio RISA disposition print pack (BHPH silo).
 * ORC 1317.12 cure notice, 1317.16 public sale, deficiency, surrender, dealer repurchase.
 * Not legal advice - counsel should review before customer use.
 */

import { money, formatDate, computeOr131712Cure, computeSaleAccounting } from './bhphMath';
import { SELLER_TITLES_EMAIL } from './printPackage';

function scrub(s) {
  return String(s ?? '')
    .replace(/\u2014/g, '-')
    .replace(/\u2013/g, '-')
    .replace(/-/g, '-')
    .replace(/-/g, '-');
}

function coNameOf(company) {
  const legal = (company?.legal_name || '').trim();
  const dba = (company?.dba || '').trim();
  if (legal && dba) return `${legal} d/b/a ${dba}`;
  return legal || dba || '[Seller Legal Name]';
}

function coAddrOf(company) {
  return [
    company?.street,
    [company?.city, company?.state || 'OH', company?.zip].filter(Boolean).join(', '),
  ]
    .filter(Boolean)
    .join(', ');
}

function emailOf(company) {
  return (company?.email || '').trim() || SELLER_TITLES_EMAIL;
}

function vehicleOf(account) {
  return [account?.veh_year, account?.veh_make, account?.veh_model, account?.veh_color]
    .filter(Boolean)
    .join(' ');
}

function buyerAddrOf(account) {
  return (
    [account?.buyer_street, account?.buyer_city, account?.buyer_state, account?.buyer_zip]
      .filter(Boolean)
      .join(', ') || '-'
  );
}

const DOC_CSS = `
body{font-family:"Times New Roman",Times,serif;margin:0.65in;color:#111;font-size:11pt;line-height:1.35}
h1{font-size:15pt;text-align:center;margin:0 0 0.35rem;font-weight:700}
h2{font-size:11.5pt;margin:0.75rem 0 0.25rem;border-bottom:1px solid #333;padding-bottom:0.1rem}
.meta{font-size:9.5pt;margin-bottom:0.55rem}
.box{border:1.5px solid #111;padding:0.5rem 0.65rem;margin:0.45rem 0}
.row{margin:0.15rem 0}
table{width:100%;border-collapse:collapse;margin:0.35rem 0;font-size:10.5pt}
th,td{border:1px solid #444;padding:0.28rem 0.35rem;text-align:left;vertical-align:top}
th{background:#f0f0f0}
td.num,th.num{text-align:right}
.sig{margin-top:1.5rem;display:grid;grid-template-columns:1fr 1fr;gap:1.75rem}
.line{border-top:1px solid #111;margin-top:2rem;padding-top:0.25rem;font-size:9pt}
.sig-date{margin-top:0.35rem;font-size:9.5pt}
.small{font-size:9pt;color:#333}
.page-break{page-break-before:always}
.check{margin:0.2rem 0}
.warn{border:2px solid #111;padding:0.4rem 0.5rem;margin:0.5rem 0;font-weight:700}
`;

function wrap(title, body) {
  return scrub(
    `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${title}</title><style>${DOC_CSS}</style></head><body>${body}</body></html>`
  );
}

function sellerBlock(company) {
  return `<div class="meta"><strong>${coNameOf(company)}</strong><br/>${coAddrOf(company) || '-'}<br/>Tel ${company?.phone || '-'} · ${emailOf(company)}<br/>County: ${company?.county || 'Lucas'}, Ohio · Dealer # ${company?.dealer_number || '-'}</div>`;
}

function openPrint(html) {
  const w = window.open('', '_blank');
  if (!w) {
    alert('Pop-up blocked. Allow pop-ups, then try again.');
    return html;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  setTimeout(() => {
    try {
      w.focus();
      w.print();
    } catch (_) {}
  }, 250);
  return html;
}

export function buildCureNotice131712(company, account, form) {
  const cure = computeOr131712Cure({
    account,
    pastDueCount: form.pastDueCount,
    lateFeeCharges: form.lateFeeCharges,
    declineFeeCharges: form.declineFeeCharges,
    otherDelinquencyCharges: form.otherDelinquencyCharges,
    repoExpense: form.repoExpense,
    storagePerDay: form.storagePerDay,
    storageDays: form.storageDays,
  });
  const repoDate = form.repoDate || formatDate(new Date()) || '-';
  const noticeDate = form.noticeDate || formatDate(new Date()) || '-';
  const cureDeadlineNote =
    'Buyer may cure within twenty (20) days after Seller retakes possession, or within fifteen (15) days after this notice is sent, whichever is later (ORC 1317.12).';

  const body = `
<h1>Notice of Default and Right to Cure After Repossession</h1>
<p style="text-align:center;font-size:10pt"><strong>Ohio Revised Code Section 1317.12</strong></p>
${sellerBlock(company)}
<div class="box">
  <div class="row"><strong>Account #:</strong> ${account.id}</div>
  <div class="row"><strong>Buyer:</strong> ${account.buyer_name || '-'}</div>
  <div class="row"><strong>Address:</strong> ${buyerAddrOf(account)}</div>
  <div class="row"><strong>Vehicle:</strong> ${vehicleOf(account) || '-'} · VIN ${account.veh_vin || '-'}</div>
  <div class="row"><strong>Date of repossession / retaking:</strong> ${repoDate}</div>
  <div class="row"><strong>Date of this notice:</strong> ${noticeDate}</div>
</div>
<h2>1. Circumstances constituting the default</h2>
<p>${scrub(form.reason || 'Failure to pay one or more installments when due under the retail installment contract.')}</p>
<h2>2. Itemized amount required to cure the default (ORC 1317.12)</h2>
<table>
  <tr><th>(A) Installments due or past due (${cure.pastDueCount} x ${money(cure.payAmt)})</th><td class="num">${money(cure.pastDueAmount)}</td></tr>
  <tr><th>(B) Unpaid delinquency / deferred charges</th><td class="num">${money(cure.delinquencyCharges)}</td></tr>
  <tr><td style="padding-left:1.25rem">Late fees</td><td class="num">${money(cure.lateFeeCharges)}</td></tr>
  <tr><td style="padding-left:1.25rem">Decline fees</td><td class="num">${money(cure.declineFeeCharges)}</td></tr>
  <tr><td style="padding-left:1.25rem">Other</td><td class="num">${money(cure.otherDelinquencyCharges)}</td></tr>
  <tr><th>(C) Actual &amp; reasonable retaking expenses (total)</th><td class="num">${money(cure.totalExpenses)}</td></tr>
  <tr><td style="padding-left:1.25rem">Repo / retaking</td><td class="num">${money(cure.repoExpense)}</td></tr>
  <tr><td style="padding-left:1.25rem">Storage (${cure.storageDays} days x ${money(cure.storagePerDay)})</td><td class="num">${money(cure.storageCost)}</td></tr>
  <tr><td style="padding-left:1.25rem"><strong>Payable at cure (door) - max $25.00 under ORC 1317.12(C)</strong></td><td class="num"><strong>${money(cure.expenseAtDoor)}</strong></td></tr>
  <tr><td style="padding-left:1.25rem">Remainder added to time balance (not required at door)</td><td class="num">${money(cure.expenseToBalance)}</td></tr>
  <tr><th>(D) Deposit of two installments (cash or bond)</th><td class="num">${money(cure.twoPayDeposit)}</td></tr>
  <tr><th><strong>TOTAL CASH / BOND TO CURE AND RECLAIM VEHICLE</strong></th><td class="num"><strong>${money(cure.cashToCure)}</strong></td></tr>
</table>
<p class="small">${cureDeadlineNote} Statutory cure under ORC 1317.12 may be exercised only once with respect to a single debt.</p>
<div class="warn">This is not a demand for the entire remaining balance. The amount above is the statutory cure package under ORC 1317.12.</div>
<h2>3. Inspection</h2>
<p class="small">During the period between retaking and expiration or exercise of the right to cure, Seller will make the Vehicle available for inspection by Buyer during reasonable hours.</p>
<h2>4. Contact</h2>
<p>To cure or arrange return of the Vehicle: ${emailOf(company)} · ${company?.phone || '-'}</p>
<div class="sig">
  <div><div class="line">Seller / authorized employee</div><div class="sig-date">Date: <strong>${noticeDate}</strong></div></div>
  <div><div class="line">Method of sending (certified mail / other)</div><div class="sig-date">Date sent: <strong>${noticeDate}</strong></div></div>
</div>
<p class="small">Keep proof of mailing. Any ORC 1317.16 sale notice may be combined with this notice when applicable.</p>`;

  return { html: wrap('ORC 1317.12 Cure Notice', body), cure };
}

export function printCureNotice131712(company, account, form) {
  const { html, cure } = buildCureNotice131712(company, account, form);
  openPrint(html);
  return { html, cure };
}

export function buildSaleNotice131716(company, account, form) {
  const saleDate = form.saleDate || '';
  const saleTime = form.saleTime || '';
  const salePlace = form.salePlace || '';
  const minPrice = money(form.minPrice || 0);
  const body = `
<h1>Notice of Public Sale of Collateral</h1>
<p style="text-align:center;font-size:10pt"><strong>Ohio Revised Code Section 1317.16</strong></p>
${sellerBlock(company)}
<div class="box">
  <div class="row"><strong>Account #:</strong> ${account.id}</div>
  <div class="row"><strong>Debtor / Buyer:</strong> ${account.buyer_name || '-'}</div>
  <div class="row"><strong>Address:</strong> ${buyerAddrOf(account)}</div>
  <div class="row"><strong>Collateral:</strong> ${vehicleOf(account) || '-'} · VIN ${account.veh_vin || '-'}</div>
  <div class="row"><strong>Stock #:</strong> ${account.veh_stock || '-'}</div>
</div>
<div class="warn">YOU MAY BE HELD LIABLE FOR ANY DEFICIENCY RESULTING FROM THIS SALE.</div>
<h2>Public sale details</h2>
<table>
  <tr><th>Date of sale</th><td>${saleDate || '____________'}</td></tr>
  <tr><th>Time of sale</th><td>${saleTime || '____________'}</td></tr>
  <tr><th>Place of sale</th><td>${salePlace || '________________________________'}</td></tr>
  <tr><th>Minimum price</th><td>${minPrice}</td></tr>
</table>
<p>Disposition will be by <strong>public sale only</strong> as required by ORC 1317.16. The method, manner, time, place, and terms shall be commercially reasonable.</p>
<p class="small">This notice is sent by certified mail, return receipt requested, to the debtor at the last address known to the secured party, and to any other persons known to have an interest in the collateral. Send at least ten (10) days prior to sale. Also publish notice of sale (listing the items) in a newspaper of general circulation in the county where the sale will be held, at least ten (10) days prior to sale.</p>
<p>Questions: ${emailOf(company)} · ${company?.phone || '-'}</p>
<div class="sig">
  <div><div class="line">Seller / authorized employee</div><div class="sig-date">Date: <strong>${form.noticeDate || form.certifiedMailSentOn || formatDate(new Date()) || '-'}</strong></div></div>
  <div><div class="line">Certified mail tracking #</div><div class="sig-date">${scrub(form.certifiedMailTracking || '____________________')}</div></div>
</div>`;
  return wrap('ORC 1317.16 Public Sale Notice', body);
}

export function printSaleNotice131716(company, account, form) {
  return openPrint(buildSaleNotice131716(company, account, form));
}

export function buildNewspaperChecklist(company, account, form) {
  const body = `
<h1>Newspaper Publication Checklist</h1>
<p style="text-align:center;font-size:10pt">ORC 1317.16 - internal checklist (attach tear sheet)</p>
${sellerBlock(company)}
<div class="box">
  <div class="row"><strong>Account #:</strong> ${account.id} · ${account.buyer_name || ''}</div>
  <div class="row"><strong>VIN:</strong> ${account.veh_vin || '-'} · ${vehicleOf(account)}</div>
  <div class="row"><strong>Sale date:</strong> ${form.saleDate || '____________'}</div>
</div>
<div class="check">[ ] Newspaper name: ${scrub(form.newspaperName || '________________________________')}</div>
<div class="check">[ ] County of publication (sale county): ${scrub(form.newspaperCounty || company?.county || 'Lucas')}</div>
<div class="check">[ ] Publication date(s): ${scrub(form.publicationDate || '____________')} (must be at least 10 days before sale)</div>
<div class="check">[ ] Notice lists items to be sold (year/make/model/VIN)</div>
<div class="check">[ ] Tear sheet / affidavit of publication attached</div>
<div class="check">[ ] Certified mail sale notice sent on: ${scrub(form.certifiedMailSentOn || '____________')}</div>
<div class="check">[ ] Tracking / receipt #: ${scrub(form.certifiedMailTracking || '____________________')}</div>
<div class="check">[ ] Co-buyer / other interest holders noticed (if any)</div>
<p class="small">File this checklist with the account. Failure to publish correctly can bar deficiency recovery under Ohio case law.</p>
<div class="line">Completed by</div>
<div class="sig-date">Date: <strong>${form.publicationDate || formatDate(new Date()) || '-'}</strong></div>`;
  return wrap('Newspaper Publication Checklist', body);
}

export function printNewspaperChecklist(company, account, form) {
  return openPrint(buildNewspaperChecklist(company, account, form));
}

export function buildSaleResultsHtml(company, account, form) {
  const acct = computeSaleAccounting({
    amountOwed: form.amountOwed,
    salePrice: form.salePrice,
    repoExpense: form.repoExpense,
    storageCost: form.storageCost,
    otherCosts: form.otherCosts,
    credits: form.credits,
    dealerBid: form.dealerIsBuyer,
  });
  const body = `
<h1>Public Sale Results / Accounting</h1>
<p style="text-align:center;font-size:10pt">BHPH account disposition (not LotSense bank accounting)</p>
${sellerBlock(company)}
<div class="box">
  <div class="row"><strong>Account #:</strong> ${account.id} · ${account.buyer_name || ''}</div>
  <div class="row"><strong>Vehicle:</strong> ${vehicleOf(account)} · VIN ${account.veh_vin || '-'}</div>
  <div class="row"><strong>Sale date:</strong> ${form.saleDate || '-'}</div>
  <div class="row"><strong>Sale place:</strong> ${scrub(form.salePlace || '-')}</div>
  <div class="row"><strong>Buyer at sale:</strong> ${scrub(form.saleBuyerName || (form.dealerIsBuyer ? coNameOf(company) : '-'))}</div>
  <div class="row"><strong>Dealer / Seller was successful bidder?</strong> ${form.dealerIsBuyer ? 'YES' : 'No'}</div>
</div>
<table>
  <tr><th>Amount owed on account (pre-sale)</th><td class="num">${money(acct.owed)}</td></tr>
  <tr><th>Repo / retaking expense</th><td class="num">${money(form.repoExpense)}</td></tr>
  <tr><th>Storage</th><td class="num">${money(form.storageCost)}</td></tr>
  <tr><th>Other allowable costs</th><td class="num">${money(form.otherCosts)}</td></tr>
  <tr><th>Credits (incl. 2-pay deposit applied, if any)</th><td class="num">${money(acct.credit)}</td></tr>
  <tr><th>Sale price / credit bid</th><td class="num">${money(acct.proceeds)}</td></tr>
  <tr><th><strong>Deficiency (if any)</strong></th><td class="num"><strong>${money(acct.deficiency)}</strong></td></tr>
  <tr><th>Surplus (if any)</th><td class="num">${money(acct.surplus)}</td></tr>
</table>
${
  form.dealerIsBuyer
    ? `<div class="box"><strong>Dealer repurchase / inventory return.</strong> Seller (or affiliate) purchased the Vehicle at the public sale for ${money(acct.proceeds)}. Vehicle may be returned to inventory for resale. Title / BMV work to be completed per Ohio dealer procedures. This does not by itself waive or create a deficiency - see separate deficiency demand or waiver.</div>`
    : ''
}
<p class="small">Attach auctioneer's report / bid sheet if used. Counsel should confirm deficiency pursuit before collection.</p>
<div class="sig">
  <div><div class="line">Prepared by</div><div class="sig-date">Date: <strong>${form.saleDate || formatDate(new Date()) || '-'}</strong></div></div>
  <div><div class="line">Manager review</div><div class="sig-date">Date: <strong>${form.saleDate || formatDate(new Date()) || '-'}</strong></div></div>
</div>`;
  return { html: wrap('Sale Results Accounting', body), accounting: acct };
}

export function printSaleResults(company, account, form) {
  const { html, accounting } = buildSaleResultsHtml(company, account, form);
  openPrint(html);
  return { html, accounting };
}

export function buildDeficiencyDemandHtml(company, account, form) {
  const amt = money(form.deficiencyAmount || 0);
  const body = `
<h1>Notice of Deficiency Balance</h1>
${sellerBlock(company)}
<div class="box">
  <div class="row"><strong>Account #:</strong> ${account.id}</div>
  <div class="row"><strong>Buyer:</strong> ${account.buyer_name || '-'}</div>
  <div class="row"><strong>Address:</strong> ${buyerAddrOf(account)}</div>
  <div class="row"><strong>Vehicle sold:</strong> ${vehicleOf(account)} · VIN ${account.veh_vin || '-'}</div>
  <div class="row"><strong>Sale date:</strong> ${form.saleDate || '-'}</div>
</div>
<p>After applying the public sale proceeds and allowable credits, a deficiency remains due under the Contract.</p>
<div class="warn">Amount claimed as deficiency: ${amt}</div>
<p>${scrub(form.deficiencyNotes || 'Payment is demanded within thirty (30) days of this notice unless other arrangements are made in writing.')}</p>
<p>Contact: ${emailOf(company)} · ${company?.phone || '-'}</p>
<div class="line">Seller</div>
<div class="sig-date">Date: <strong>${form.saleDate || formatDate(new Date()) || '-'}</strong></div>`;
  return wrap('Deficiency Demand', body);
}

export function printDeficiencyDemand(company, account, form) {
  return openPrint(buildDeficiencyDemandHtml(company, account, form));
}

export function buildDeficiencyWaiverHtml(company, account, form) {
  const body = `
<h1>Deficiency Waived - Account Closed</h1>
<p style="text-align:center;font-size:10pt">No further chase / collection of deficiency</p>
${sellerBlock(company)}
<div class="box">
  <div class="row"><strong>Account #:</strong> ${account.id}</div>
  <div class="row"><strong>Buyer:</strong> ${account.buyer_name || '-'}</div>
  <div class="row"><strong>Vehicle:</strong> ${vehicleOf(account)} · VIN ${account.veh_vin || '-'}</div>
  <div class="row"><strong>Sale / close date:</strong> ${form.saleDate || form.closedOn || formatDate(new Date()) || '-'}</div>
  <div class="row"><strong>Calculated deficiency (if any):</strong> ${money(form.deficiencyAmount || 0)}</div>
</div>
<p>Seller elects <strong>not to pursue</strong> any deficiency balance against Buyer arising from the disposition of the Vehicle described above. For BHPH portfolio purposes, this account is <strong>closed - deficiency waived</strong>.</p>
<p class="small">This waiver is a business decision by Seller. It does not admit improper notice or sale. Vehicle title/inventory handling (including dealer purchase at public sale) is documented separately.</p>
<p>${scrub(form.waiverNotes || '')}</p>
<div class="sig">
  <div><div class="line">Seller authorization</div><div class="sig-date">Date: <strong>${form.saleDate || form.closedOn || formatDate(new Date()) || '-'}</strong></div></div>
  <div><div class="line">File copy</div></div>
</div>`;
  return wrap('Deficiency Waived', body);
}

export function printDeficiencyWaiver(company, account, form) {
  return openPrint(buildDeficiencyWaiverHtml(company, account, form));
}

export function buildVoluntarySurrenderHtml(company, account, form) {
  const body = `
<h1>Voluntary Surrender of Vehicle</h1>
${sellerBlock(company)}
<div class="box">
  <div class="row"><strong>Account #:</strong> ${account.id}</div>
  <div class="row"><strong>Buyer:</strong> ${account.buyer_name || '-'}</div>
  <div class="row"><strong>Address:</strong> ${buyerAddrOf(account)}</div>
  <div class="row"><strong>Vehicle:</strong> ${vehicleOf(account)} · VIN ${account.veh_vin || '-'}</div>
  <div class="row"><strong>Surrender date:</strong> ${form.surrenderDate || formatDate(new Date()) || '-'}</div>
  <div class="row"><strong>Odometer at surrender:</strong> ${form.surrenderOdometer || '____________'}</div>
  <div class="row"><strong>Keys turned in?</strong> ${form.keysTurnedIn ? 'Yes' : 'No'} ${form.keysTurnedIn ? '(key turn-in bonus may apply per Contract)' : ''}</div>
</div>
<p>Buyer voluntarily surrenders the Vehicle to Seller. Buyer acknowledges that surrender does not, by itself, satisfy the Contract balance. Seller may dispose of the Vehicle as allowed by Ohio law (including ORC 1317.12 / 1317.16 where applicable) and may pursue or waive any deficiency as stated in later notices.</p>
<p>Reason / notes: ${scrub(form.surrenderNotes || '-')}</p>
<div class="sig">
  <div><div class="line">Buyer signature</div><div class="sig-date">Date: <strong>${form.surrenderDate || formatDate(new Date()) || '-'}</strong></div></div>
  <div><div class="line">Co-Buyer signature</div><div class="sig-date">Date: <strong>${form.surrenderDate || formatDate(new Date()) || '-'}</strong></div></div>
</div>
<div class="sig">
  <div><div class="line">Seller received Vehicle</div><div class="sig-date">Date: <strong>${form.surrenderDate || formatDate(new Date()) || '-'}</strong></div></div>
  <div><div class="line">Condition notes</div></div>
</div>`;
  return wrap('Voluntary Surrender', body);
}

export function printVoluntarySurrender(company, account, form) {
  return openPrint(buildVoluntarySurrenderHtml(company, account, form));
}

export function buildDealerRepurchaseHtml(company, account, form) {
  const bid = money(form.salePrice || form.dealerBidAmount || 0);
  const body = `
<h1>Dealer Purchase at Public Sale / Inventory Return</h1>
<p style="text-align:center;font-size:10pt">Credit bid or purchase by Seller - Vehicle returned to inventory</p>
${sellerBlock(company)}
<div class="box">
  <div class="row"><strong>Account #:</strong> ${account.id} · ${account.buyer_name || ''}</div>
  <div class="row"><strong>Vehicle:</strong> ${vehicleOf(account)} · VIN ${account.veh_vin || '-'}</div>
  <div class="row"><strong>Public sale date:</strong> ${form.saleDate || '-'}</div>
  <div class="row"><strong>Sale place:</strong> ${scrub(form.salePlace || '-')}</div>
  <div class="row"><strong>Seller / dealer bid (purchase price):</strong> ${bid}</div>
  <div class="row"><strong>Minimum price noticed:</strong> ${money(form.minPrice || 0)}</div>
</div>
<p>Seller (or its authorized representative) was the successful bidder at the ORC 1317.16 public sale and purchases the Vehicle for <strong>${bid}</strong>. The Vehicle is transferred to Seller's inventory for reconditioning and resale. BMV / title work shall be completed as required for dealer inventory.</p>
<p><strong>Balance treatment:</strong> ${
    form.closePath === 'waive'
      ? `Seller elects to waive any deficiency and close the BHPH account (see Deficiency Waived document).`
      : form.closePath === 'pursue'
        ? `Seller may pursue any remaining deficiency after applying this purchase price (see Deficiency Demand).`
        : `See separate sale accounting and deficiency demand or waiver.`
  }</p>
<p class="small">Note: Purchase price may be less than the unpaid contract balance. Paying/bidding less than the balance is common at foreclosure sales; document the public sale process. This BHPH form does not create LotSense Accounting bank entries.</p>
<div class="sig">
  <div><div class="line">Seller authorization</div><div class="sig-date">Date: <strong>${form.saleDate || formatDate(new Date()) || '-'}</strong></div></div>
  <div><div class="line">Inventory / title clerk</div><div class="sig-date">Date: <strong>${form.saleDate || formatDate(new Date()) || '-'}</strong></div></div>
</div>`;
  return wrap('Dealer Purchase at Public Sale', body);
}

export function printDealerRepurchase(company, account, form) {
  return openPrint(buildDealerRepurchaseHtml(company, account, form));
}
